import { FC, Fragment, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
// Redux
import { RootState } from 'store';
import { useAppSelector } from 'hooks/useStore';
import { selectBrandConsents, selectConsents, selectHasConsents, selectPlatformConsents } from 'store/consents/consentsSelectors';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Components
import ConsentItem from './ConsentItem';
import { Button } from 'components/Buttons';
import { Checkbox } from 'components/Controls';
// i18next
import { useTranslation } from 'react-i18next';

const SectionConsents:FC = () => {
  const { t } = useTranslation();

  const { control, watch, setValue } = useFormContext();

  const languageWatcher = watch('language');
  const registrationPageWatcher = watch('registrationPage');

  const quiz = useAppSelector(selectQuiz);
  const consents = useAppSelector(selectConsents);
  const hasConsents = useAppSelector((state:RootState) => selectHasConsents(state, { lng: languageWatcher }));
  const platformConsents = useAppSelector((state:RootState) => selectPlatformConsents(state, { lng: languageWatcher }));
  const brandConsents = useAppSelector((state:RootState) => selectBrandConsents(state, { lng: languageWatcher }));
  const [selectedConsents, setSelectedConsents] = useState<string[]>([]);
  const [selectedConsentsLinks, setSelectedConsentsLinks] = useState<string[]>([]);

  const handleSelect = (consentId: string) => {
    if (selectedConsents.includes(consentId)) {
      setSelectedConsents(prev => prev.filter(id => id !== consentId));
    } else {
      setSelectedConsents(prev => ([...prev, consentId]));
    }
  }

  const handleSelectConsentLink = (consentId: string) => {
    if (selectedConsentsLinks.includes(consentId)) {
      setSelectedConsentsLinks(prev => prev.filter(id => id !== consentId));
    } else {
      setSelectedConsentsLinks(prev => ([...prev, consentId]));
    }
  }

  useEffect(() => {
    setValue('consents', selectedConsents);
    // eslint-disable-next-line
  }, [selectedConsents]);

  useEffect(() => {
    setValue('consentsLinks', selectedConsentsLinks);
    // eslint-disable-next-line
  }, [selectedConsentsLinks]);
  
  useEffect(() => {
    if (!consents) return;

    const filteredConsents = consents.filter(consent => consent.language === languageWatcher);
    const quizConsents = quiz?.consents?.filter(consentId => filteredConsents.find(consent => consent._id === consentId)) || [];
    const quizConsentsLinks = quiz?.consentsLinks?.filter(consentId => filteredConsents.find(consent => consent._id === consentId)) || [];
    const requiredConsentsIds = filteredConsents.filter(consent => (consent.language === languageWatcher && consent.required)).map(c => c._id);
    const set = new Set([...quizConsents, ...requiredConsentsIds]);

    setSelectedConsents([...set]);
    setSelectedConsentsLinks(quizConsentsLinks);
  }, [consents, languageWatcher, quiz]);

  if (registrationPageWatcher?.anonymous !== false) return null;
  if (!platformConsents && !brandConsents) return null;
  return (
    <div>
      <div className="divider my-4" />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h4 className="text-xl sm:text-2xl font-semibold mb-4 text-white">{t('pages.quizForm.consentsLabel')}</h4>
        <NavLink to="/owner/consents" target="_blank">
          <Button variant="contained">
            {t('pages.consents.title')}
          </Button>
        </NavLink>
      </div>
      <Controller
        control={control} name="registrationPage.showAgreeAllCheckbox"
        render={({ field: { onChange, value } }) => (
          <Checkbox
            id="showAgreeAllCheckbox" name="showAgreeAllCheckbox"
            label={t('pages.quizForm.showAgreeAllCheckboxLabel')}
            checked={value}
            onChange={onChange}
            helperText={t('pages.quizForm.showAgreeAllCheckboxHint')}
          />
        )}
      />
      <Controller
        control={control} name="registrationPage.showRequiredHint"
        render={({ field: { onChange, value } }) => (
          <Checkbox
            id="showRequiredHint" name="showRequiredHint"
            label={t('pages.quizForm.showRequiredHintLabel')}
            checked={value}
            onChange={onChange}
            helperText={t('pages.quizForm.showRequiredHintHint')}
          />
        )}
      />
      {platformConsents?.length ? (
        <div className="mb-8">
          <span className="block text-white text-base font-semibold mr-3 mb-1">{t('pages.quizForm.platformConsentsLabel')}</span>
          <span className="block text-white text-xs mr-3 mb-3">{t('pages.quizForm.platformConsentsHint')}</span>
          {platformConsents.map(consent => (
            <ConsentItem
              key={consent._id}
              consent={consent}
              checked={selectedConsents.includes(consent._id)}
              handleSelect={handleSelect}
            />
          ))}
        </div>
      ) : null}
      {brandConsents?.length ? (
        <Fragment>
          <div className="mb-8">
            <span className="block text-white text-base font-semibold mr-3 mb-1">{t('pages.quizForm.accountConsentsLabel')}</span>
            <span className="block text-white text-xs mr-3 mb-3">{t('pages.quizForm.accountConsentsHint')}</span>
            {brandConsents.map(consent => (
              <ConsentItem
                key={consent._id}
                consent={consent}
                checked={selectedConsents.includes(consent._id)}
                handleSelect={handleSelect}
              />
            ))}
          </div>

          <div>
            <span className="block text-white text-base font-semibold mr-3 mb-1">{t('pages.quizForm.consentsLinksLabel')}</span>
            <span className="block text-white text-xs mr-3 mb-3">{t('pages.quizForm.consentsLinksHint')}</span>
            {brandConsents.map(consent => (
              <ConsentItem
                key={`consent-link-${consent._id}`}
                consent={consent}
                checked={selectedConsentsLinks.includes(consent._id)}
                handleSelect={handleSelectConsentLink}
                isLink
              />
            ))}
          </div>
        </Fragment>
      ) : null}
      {!hasConsents && (
        <span className="block text-white text-sm font-semibold mr-3">{t('pages.quizForm.noConsentData')}</span>
      )}
    </div>
  )
}

export default SectionConsents;
